import {SagaIterator} from 'redux-saga';

import {call, fork, getContext, put, putResolve, takeEvery} from 'redux-saga/effects';

import {SEGMENT_FOR_MENTION_ME} from 'constants/analytics/mentionMe';
import {ENDPOINTS} from 'constants/endpoints';
import {CartType} from 'desktop/pages/cart/types';
import {PAYMENTS} from 'desktop/pages/checkout/constants/paymentStep';
import {setSelectedMethod} from 'desktop/pages/checkout/store/reducers/paymentStep/selectedMethod/selectedMethod';
import {saveOrderDetails} from 'desktop/pages/checkout/store/sagas/analytics/analytics';
import {SaveApplePayExpressType} from 'desktop/types/payPalExpressType';
import {handlePayPalDesktopErrorAnalytic} from 'features/expressPayments/payPalV3/utils/analytics';
import {getAffId} from 'libs/analytics/affiliate';
import {apiConfig} from 'libs/api/api';
import {logWithSentry} from 'libs/sentry/sentry';

import {localStore} from 'libs/storage/storage';

import {ActionType} from 'types/reducers';

import {
    savePayPalExpress,
    savePayPalExpressOrder,
    savePayPalExpressPayment,
    setPayPalExpressLoading,
} from '../../../reducers/payments/paypalExpress/paypalExpress';
import {getPayPalOrderDataFromCart} from '../../../selectors/payments/payPalExpress/payPalExpress';

/**
 * Extracts token from backend request
 * @param {string} approveUrl - https://www.paypal.com/checkoutnow?token=78E910950X4861440
 */
const extractPayPalToken = (approveUrl: string) => {
    let token;
    try {
        token = new URL(approveUrl).searchParams.get('token');
    } catch (e) {
        logWithSentry(e, 'Paypal token is not valid, order creation failed on backend');
    }
    return token;
};

/**
 * Get token for paypal, both used in checkout & cart
 */
export async function getTokenPayPalExpress(cart: CartType) {
    const {data} = await apiConfig.instanceGusaBackend({
        baseURL: apiConfig.API_URL,
        data: getPayPalOrderDataFromCart(cart),
        method: 'post',
        url: ENDPOINTS.PAYPAL_TOKEN,
    });
    return {id: data.id, token: extractPayPalToken(data.approveUrl)};
}

/**
 * Redirect to success page if payment request is success
 */
export function* redirectToSuccessPage(): SagaIterator {
    const navigate = yield getContext('navigate');
    yield call(navigate, '/checkout/onepage/success/');
}

/**
 * Call saveOrder and redirect to success page
 */
export function* placeOrderPayPalExpress() {
    const {search} = apiConfig.hostStore?.getState()?.router?.location;
    const affid: string = yield call(getAffId, {search});

    const completeOrder: {action?: {payload?: {data?: {mention_me_segment?: string}}}} =
        yield putResolve(savePayPalExpressOrder(affid));

    yield call(
        [localStore, 'set'],
        SEGMENT_FOR_MENTION_ME,
        completeOrder?.action?.payload?.data?.mention_me_segment || '',
    );
    yield put(setPayPalExpressLoading(false));
    yield call(redirectToSuccessPage);
}

/**
 * Call save and after that saveOrder endpoints
 */
export function* createPayPalExpressOrder({
    payload,
}: ActionType<SaveApplePayExpressType>): SagaIterator {
    try {
        yield put(setPayPalExpressLoading(true));

        yield put(setSelectedMethod(PAYMENTS.METHOD_PAYPAL_V3));
        yield call(saveOrderDetails);
        yield putResolve(savePayPalExpressPayment(payload));

        yield fork(placeOrderPayPalExpress);
    } catch (e) {
        handlePayPalDesktopErrorAnalytic(e);
        yield put(setPayPalExpressLoading(false));
    }
}

/**
 * Saga for payPalExpress
 * */
export function* payPalExpressPaymentSaga(): SagaIterator {
    yield takeEvery(`${savePayPalExpress}`, createPayPalExpressOrder);
}
